// src/pages/onboarding/FirstRunPrivacy.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../shared/api/axios';
import { useTranslation } from 'react-i18next';

type Prefs = {
  analyticsConsent: boolean;
  pushConsent: boolean;
  locationConsent: boolean;
};

const defaultPrefs: Prefs = {
  analyticsConsent: false,
  pushConsent: false,
  locationConsent: false,
};

const ToggleRow: React.FC<{
  title: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  yesLabel: string;
  noLabel: string;
}> = ({ title, description, checked, onChange, yesLabel, noLabel }) => {
  return (
    <div style={row}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{title}</div>
        {description ? <div style={desc}>{description}</div> : null}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
        style={{
          ...toggleBtn,
          background: checked ? '#10B981' : '#EF4444', // green / red
        }}
      >
        {checked ? yesLabel : noLabel}
      </button>
    </div>
  );
};

const FirstRunPrivacy: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [prefs, setPrefs] = useState<Prefs>(defaultPrefs);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // 1) Try to load existing preferences from backend (fallback to defaults)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/privacy/preferences');
        setPrefs({ ...defaultPrefs, ...data });
      } catch {
        setPrefs(defaultPrefs);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setKey =
    (key: keyof Prefs) =>
    (v: boolean) =>
      setPrefs((p) => ({ ...p, [key]: v }));

  // 2) Save and continue → Gallery
  const handleContinue = async () => {
    try {
      setSaving(true);
      setError('');
      await api.put('/privacy/preferences', { ...prefs, firstRunComplete: true });
      navigate('/gallery', { replace: true });
    } catch (e: any) {
      setError(
        e?.response?.data?.msg || t('firstRunPrivacy.errorSave')
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={wrap}>
        <h2 style={title}>{t('firstRunPrivacy.title')}</h2>
        <p>{t('firstRunPrivacy.loading')}</p>
      </div>
    );
  }

  return (
    <div style={wrap}>
      <h2 style={title}>{t('firstRunPrivacy.title')}</h2>

      <div style={card}>
        <ToggleRow
          title={t('firstRunPrivacy.toggles.locationTitle')}
          description={t('firstRunPrivacy.toggles.locationDesc')}
          checked={prefs.locationConsent}
          onChange={setKey('locationConsent')}
          yesLabel={t('firstRunPrivacy.yes')}
          noLabel={t('firstRunPrivacy.no')}
        />
        <ToggleRow
          title={t('firstRunPrivacy.toggles.analyticsTitle')}
          description={t('firstRunPrivacy.toggles.analyticsDesc')}
          checked={prefs.analyticsConsent}
          onChange={setKey('analyticsConsent')}
          yesLabel={t('firstRunPrivacy.yes')}
          noLabel={t('firstRunPrivacy.no')}
        />
        <ToggleRow
          title={t('firstRunPrivacy.toggles.pushTitle')}
          description={t('firstRunPrivacy.toggles.pushDesc')}
          checked={prefs.pushConsent}
          onChange={setKey('pushConsent')}
          yesLabel={t('firstRunPrivacy.yes')}
          noLabel={t('firstRunPrivacy.no')}
        />

        {error && <div style={errBox}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={handleContinue}
            disabled={saving}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              color: '#fff',
              background: 'rgb(56, 6, 117)', // violet
              cursor: 'pointer',
              minWidth: 140,
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving
              ? t('firstRunPrivacy.saving')
              : t('firstRunPrivacy.continue')}
          </button>
        </div>

        <div
          style={{
            marginTop: 10,
            fontSize: 12,
            color: '#6b7280',
          }}
        >
          {t('firstRunPrivacy.footerNote')}
        </div>
      </div>
    </div>
  );
};

/* ——— styles ——— */
const wrap: React.CSSProperties = {
  maxWidth: 720,
  margin: '80px auto',
  padding: '0 16px',
};
const title: React.CSSProperties = {
  fontSize: 24,
  marginBottom: 14,
};
const card: React.CSSProperties = {
  border: '1px solid #eee',
  borderRadius: 10,
  padding: 16,
  background: '#fff',
};
const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 0',
  borderBottom: '1px solid #f1f5f9',
};
const desc: React.CSSProperties = { color: '#6b7280', fontSize: 13, marginTop: 4 };
const toggleBtn: React.CSSProperties = {
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '8px 14px',
  cursor: 'pointer',
  minWidth: 70,
  fontWeight: 600,
};
const errBox: React.CSSProperties = {
  background: '#fff7ed',
  border: '1px solid #fdba74',
  color: '#7c2d12',
  padding: 10,
  borderRadius: 8,
  marginTop: 10,
};

export default FirstRunPrivacy;
